import type { JSONContent, Editor } from '@tiptap/core';
import { useEditor } from '@tiptap/react';
import { useEffect } from 'react';
import type { RefObject } from 'react';

import { shouldIgnoreRichTextBlur } from './richTextBlurGuard';
import { useRichTextEditorAutoFocus } from './richTextAutoFocus';

type UseEditorOptions = NonNullable<Parameters<typeof useEditor>[0]>;
type RichTextFieldEditorExtensions = UseEditorOptions['extensions'];

interface UseRichTextFieldEditorOptions {
  extensions: RichTextFieldEditorExtensions;
  content: JSONContent;
  onContentChange: (content: JSONContent) => void;
  onFocus: () => void;
  onBlur: () => void;
  containerRef: RefObject<HTMLElement | null>;
  editorClassName?: string;
  editorInlineStyle: string;
  autoFocus: boolean;
  shouldRerenderOnTransaction?: boolean;
}

export function useRichTextFieldEditor({
  extensions,
  content,
  onContentChange,
  onFocus,
  onBlur,
  containerRef,
  editorClassName,
  editorInlineStyle,
  autoFocus,
  shouldRerenderOnTransaction = false,
}: UseRichTextFieldEditorOptions): Editor | null {
  const editor = useEditor({
    extensions,
    immediatelyRender: false,
    ...(shouldRerenderOnTransaction ? { shouldRerenderOnTransaction: true } : {}),
    content,
    onUpdate: ({ editor: currentEditor }) => {
      onContentChange(currentEditor.getJSON());
    },
    onFocus,
    onBlur: ({ event }) => {
      if (shouldIgnoreRichTextBlur(containerRef.current, event?.relatedTarget)) {
        return;
      }

      onBlur();
    },
    editorProps: {
      attributes: {
        ...(editorClassName ? { class: editorClassName } : {}),
        style: editorInlineStyle,
      },
    },
  });

  useRichTextEditorAutoFocus(editor, autoFocus);

  // Sync external content changes into the editor. TipTap owns its own document
  // instance, so a changed `content` prop (e.g. the store reseeded on a
  // client-context switch, or a fresh refetch) is otherwise ignored and the
  // field keeps showing the previous context's text. We only push when the
  // incoming document differs from what the editor already holds — during normal
  // typing `content` mirrors the editor's own onUpdate output, so the cursor is
  // never reset. Schema defaults and JSON key order do not count as changes.
  // `emitUpdate: false` suppresses the update event, avoiding an
  // onChange loop.
  useEffect(() => {
    if (!editor) return;
    try {
      const incoming = editor.schema.nodeFromJSON(content);
      if (editor.state.doc.eq(incoming)) return;
    } catch {
      // Let setContent retain its existing invalid-content handling.
    }
    editor.commands.setContent(content, { emitUpdate: false });
  }, [content, editor]);

  return editor;
}
